import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CategoryGroupChat } from './CategoryGroupChat';
import {
    // Layout & Navigation
    Plus, ArrowRight, Trash2, Edit2,
    // General & Popular
    Lightbulb, Wrench, Code, Palette, Music, Camera, Book, GraduationCap,
    Heart, Coffee, Leaf, Dumbbell, Briefcase, Home, Car, Plane, Globe, Star, Sparkles,
    Zap, Award, Target, Rocket, Shield, Users, MessageCircle, Mail, Phone, MapPin,
    // Creative & Arts
    Brush, Pencil, PenTool, Film, Mic, Radio, Tv, Image, Video, Headphones, Guitar,
    // Tech & Development
    Laptop, Monitor, Smartphone, Tablet, Database, Server, Cpu, HardDrive, Wifi, Cloud,
    // Business & Finance
    TrendingUp, DollarSign, CreditCard, Calculator, PieChart, BarChart, Clipboard, FileText,
    // Education & Learning
    BookOpen, Notebook, Library, School, Presentation, Languages, BrainCircuit, Trophy,
    // Health & Wellness
    Activity, HeartPulse, Stethoscope, Pill, Apple, Salad, Smile, Moon, Sun,
    // Sports & Fitness
    Bike, Medal, Flame, Timer, Waves, BicepsFlexed,
    // Food & Cooking
    UtensilsCrossed, ChefHat, Pizza, CakeSlice, Cookie, IceCream, Wine,
    // Nature & Environment
    Trees, Flower, Flower2, Sprout, Bug, Bird, Fish, Mountain,
    // Travel & Places
    Building, Building2, Store, Map, Compass, Luggage, Hotel, Landmark,
    // Tools & Construction
    Hammer, Drill, Paintbrush, Ruler, Scissors, Settings, Cog, Box, Package,
    // Communication & Social
    Share2, Send, Megaphone, Volume2, Bell, MessageSquare, UserPlus, Handshake, Gift,
    // Time & Schedule
    Clock, Calendar, CalendarDays, AlarmClock, Hourglass, Watch,
    // Transportation
    Bus, Train, Ship, Truck, Fuel,
    // Animals & Pets
    Dog, Cat, Rabbit, Turtle, Bone, Squirrel,
} from 'lucide-react';

const ICON_MAP = {
    Lightbulb, Wrench, Code, Palette, Music, Camera, Book, GraduationCap,
    Heart, Coffee, Leaf, Dumbbell, Briefcase, Home, Car, Plane, Globe,
    Star, Sparkles, Zap, Award, Target, Rocket, Shield, Users,
    MessageCircle, Mail, Phone, MapPin,
    Brush, Pencil, PenTool, Film, Mic, Radio, Tv, Image, Video, Headphones, Guitar,
    Laptop, Monitor, Smartphone, Tablet, Database, Server, Cpu, HardDrive, Wifi, Cloud,
    TrendingUp, DollarSign, CreditCard, Calculator, PieChart, BarChart, Clipboard, FileText,
    BookOpen, Notebook, Library, School, Presentation, Languages, BrainCircuit, Trophy,
    Activity, HeartPulse, Stethoscope, Pill, Apple, Salad, Smile, Moon, Sun,
    Bike, Medal, Flame, Timer, Waves, BicepsFlexed,
    UtensilsCrossed, ChefHat, Pizza, CakeSlice, Cookie, IceCream, Wine,
    Trees, Flower, Flower2, Sprout, Bug, Bird, Fish, Mountain,
    Building, Building2, Store, Map, Compass, Luggage, Hotel, Landmark,
    Hammer, Drill, Paintbrush, Ruler, Scissors, Settings, Cog, Box, Package,
    Share2, Send, Megaphone, Volume2, Bell, MessageSquare, UserPlus, Handshake, Gift,
    Clock, Calendar, CalendarDays, AlarmClock, Hourglass, Watch,
    Bus, Train, Ship, Truck, Fuel,
    Dog, Cat, Rabbit, Turtle, Bone, Squirrel,
};

const getIconComponent = (iconName) => ICON_MAP[iconName] || Lightbulb;

// Rotating accent palette — mirrors the bento home page cards.
// Uses only shades defined in tailwind.config.js (status colors expose 50/100/500/600/700).
const CATEGORY_ACCENTS = [
    { gradient: 'bg-gradient-to-br from-brand-500 to-brand-600',         blob: 'bg-brand-200/50' },
    { gradient: 'bg-gradient-to-br from-warning-500 to-warning-600',     blob: 'bg-warning-100/70' },
    { gradient: 'bg-gradient-to-br from-info-500 to-info-600',           blob: 'bg-info-100/70' },
    { gradient: 'bg-gradient-to-br from-success-500 to-success-600',     blob: 'bg-success-100/70' },
    { gradient: 'bg-gradient-to-br from-secondary-500 to-secondary-600', blob: 'bg-secondary-200/50' },
    { gradient: 'bg-gradient-to-br from-danger-500 to-danger-600',       blob: 'bg-danger-100/70' },
];

// Stable hash → consistent accent per category name across renders
const accentForCategory = (name) => {
    let h = 0;
    for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) | 0;
    return CATEGORY_ACCENTS[Math.abs(h) % CATEGORY_ACCENTS.length];
};

const getCategoryIcon = (type) => (type === 'skill' ? BicepsFlexed : Lightbulb);

export const SkillsKnowledgeSection = ({
    skillsKnowledge,
    chatId,
    zoneId,
    userName,
    userPhoto,
    onAddClick,
    onDeleteSkill,
    onEditSkill,
}) => {
    const [skillsFilter, setSkillsFilter] = useState('skill');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [showChat, setShowChat] = useState(false);

    const groupedByCategory = skillsKnowledge
        .filter(item => item.type === skillsFilter)
        .reduce((acc, item) => {
            const cats = item.category ? item.category.split(',').map(c => c.trim()) : ['ללא קטגוריה'];
            cats.forEach(cat => {
                if (!acc[cat]) acc[cat] = [];
                acc[cat].push(item);
            });
            return acc;
        }, {});

    const selectedCategoryItems = selectedCategory
        ? skillsKnowledge.filter(item =>
            item.type === skillsFilter &&
            item.category &&
            item.category.split(',').map(c => c.trim()).includes(selectedCategory)
        )
        : [];

    const totalForFilter = skillsKnowledge.filter(item => item.type === skillsFilter).length;
    const FilterIcon = getCategoryIcon(skillsFilter);

    return (
        <div className="space-y-6">
            <AnimatePresence mode="wait">
                {!selectedCategory ? (
                    <motion.div
                        key="categories"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="space-y-6"
                    >
                        {/* Section header — matches the Members tab pattern */}
                        <div className="flex items-end justify-between gap-4 flex-wrap">
                            <div>
                                <p className="eyebrow text-brand-700 mb-1">קהילה</p>
                                <h2 className="heading-lg text-ink">כישורים וידע</h2>
                                <p className="text-sm text-ink-muted mt-1 max-w-md">
                                    גלו את המומחיות שהקהילה מציעה — או שתפו את שלכם. בחרו קטגוריה כדי לראות חברים ולפתוח צ׳אט.
                                </p>
                            </div>
                            {chatId && chatId !== 'guest' && (
                                <motion.button
                                    onClick={() => onAddClick(skillsFilter)}
                                    whileHover={{ y: -1 }}
                                    whileTap={{ scale: 0.97 }}
                                    className="inline-flex items-center gap-2 h-11 px-5 rounded-pill bg-brand-500 hover:bg-brand-600 text-ink-inverse font-bold text-sm shadow-card hover:shadow-elevated transition-shadow"
                                >
                                    <Plus size={16} strokeWidth={2.5} />
                                    <span>שתפו {skillsFilter === 'skill' ? 'כישור' : 'ידע'}</span>
                                </motion.button>
                            )}
                        </div>

                        {/* Segmented filter — pill style matching community filter chips */}
                        <div className="inline-flex items-center gap-1.5 p-1 rounded-pill bg-surface-muted ring-1 ring-line-subtle">
                            {[
                                { value: 'skill',     label: 'כישורים',         icon: BicepsFlexed },
                                { value: 'knowledge', label: 'ידע ותחומי עניין', icon: Lightbulb },
                            ].map(({ value, label, icon: Icon }) => {
                                const active = skillsFilter === value;
                                return (
                                    <button
                                        key={value}
                                        type="button"
                                        onClick={() => setSkillsFilter(value)}
                                        aria-pressed={active}
                                        className={`relative inline-flex items-center gap-1.5 px-4 py-1.5 rounded-pill text-sm font-semibold transition-[background-color,color,box-shadow] duration-200 ease-smooth focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2 ${
                                            active
                                                ? 'bg-brand-500 text-ink-inverse shadow-sm'
                                                : 'text-ink-muted hover:text-brand-700'
                                        }`}
                                    >
                                        <Icon className="w-4 h-4" strokeWidth={2} />
                                        <span>{label}</span>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Category bento grid */}
                        {totalForFilter > 0 ? (
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={skillsFilter}
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -4 }}
                                    transition={{ duration: 0.25 }}
                                    className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2.5 sm:gap-3"
                                >
                                    {Object.entries(groupedByCategory)
                                        .sort(([a], [b]) => a.localeCompare(b, 'he'))
                                        .map(([category, items], index) => {
                                            const accent = accentForCategory(category);
                                            return (
                                                <motion.button
                                                    key={`${skillsFilter}-${category}`}
                                                    type="button"
                                                    onClick={() => setSelectedCategory(category)}
                                                    initial={{ opacity: 0, y: 8 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ delay: Math.min(index * 0.035, 0.32), duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                                    whileHover={{ y: -2 }}
                                                    whileTap={{ scale: 0.98 }}
                                                    className="group relative text-right overflow-hidden rounded-2xl bg-surface border border-line-subtle hover:border-line shadow-card hover:shadow-elevated transition-shadow duration-300 p-3.5 sm:p-4 flex flex-col focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
                                                >
                                                    <div aria-hidden="true" className={`pointer-events-none absolute -top-10 -start-8 w-24 h-24 rounded-pill blur-2xl opacity-60 ${accent.blob}`} />

                                                    <div className="relative flex items-center justify-between mb-2">
                                                        <div className={`inline-flex items-center justify-center w-9 h-9 rounded-xl shadow-sm text-white ${accent.gradient}`}>
                                                            <FilterIcon size={18} strokeWidth={2.25} />
                                                        </div>
                                                        <span className="text-2xl sm:text-3xl font-bold text-ink-strong leading-none tabular-nums">
                                                            {items.length}
                                                        </span>
                                                    </div>

                                                    <div className="relative">
                                                        <h3 className="text-sm sm:text-base font-bold text-ink-strong leading-tight line-clamp-2">
                                                            {category}
                                                        </h3>
                                                        <p className="text-2xs sm:text-xs text-ink-muted mt-0.5 leading-snug">
                                                            {items.length} {items.length === 1 ? 'חבר' : 'חברים'}
                                                        </p>
                                                    </div>
                                                </motion.button>
                                            );
                                        })}
                                </motion.div>
                            </AnimatePresence>
                        ) : (
                            /* Empty state — matches home page pattern */
                            <div className="flex flex-col items-center justify-center text-center py-16 px-6 surface-soft rounded-2xl max-w-2xl mx-auto">
                                <div className="relative mb-5">
                                    <span aria-hidden="true" className="absolute inset-0 bg-brand-200/40 rounded-pill blur-2xl" />
                                    <span className="relative inline-flex items-center justify-center w-20 h-20 rounded-pill bg-surface ring-4 ring-brand-50">
                                        <FilterIcon className="w-9 h-9 text-brand-500" strokeWidth={1.75} />
                                    </span>
                                </div>
                                <h3 className="heading-sm text-ink mb-2">
                                    {skillsFilter === 'skill' ? 'אין עדיין כישורים' : 'אין עדיין ידע'}
                                </h3>
                                <p className="text-sm text-ink-muted max-w-xs mb-5">
                                    היו הראשונים לשתף — זה לוקח רגע ועוזר לכולם.
                                </p>
                                {chatId && chatId !== 'guest' && (
                                    <motion.button
                                        onClick={() => onAddClick(skillsFilter)}
                                        whileHover={{ y: -1 }}
                                        whileTap={{ scale: 0.97 }}
                                        className="inline-flex items-center gap-2 h-11 px-5 rounded-pill bg-brand-500 hover:bg-brand-600 text-ink-inverse font-bold text-sm shadow-card hover:shadow-elevated transition-shadow"
                                    >
                                        <Plus size={16} strokeWidth={2.5} />
                                        <span>הוסיפו עכשיו</span>
                                    </motion.button>
                                )}
                            </div>
                        )}
                    </motion.div>
                ) : (
                    <motion.div
                        key="details"
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="space-y-6"
                    >
                        {/* Category detail header */}
                        {(() => {
                            const accent = accentForCategory(selectedCategory);
                            return (
                                <div className="flex items-start justify-between gap-4 flex-wrap">
                                    <div className="flex items-start gap-4 min-w-0">
                                        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl shadow-sm text-white shrink-0 ${accent.gradient}`}>
                                            <FilterIcon size={22} strokeWidth={2.25} />
                                        </div>
                                        <div className="min-w-0">
                                            <button
                                                type="button"
                                                onClick={() => setSelectedCategory(null)}
                                                className="inline-flex items-center gap-1 text-xs font-semibold text-brand-700 hover:text-brand-800 mb-1.5 group"
                                            >
                                                <ArrowRight size={14} strokeWidth={2.5} className="group-hover:translate-x-0.5 transition-transform" />
                                                <span>חזרה לקטגוריות</span>
                                            </button>
                                            <h2 className="heading-lg text-ink leading-tight truncate">{selectedCategory}</h2>
                                            <p className="text-sm text-ink-muted mt-0.5">
                                                {selectedCategoryItems.length} {selectedCategoryItems.length === 1 ? 'חבר' : 'חברים'} בקטגוריה זו
                                            </p>
                                        </div>
                                    </div>
                                    <motion.button
                                        onClick={() => setShowChat(true)}
                                        whileHover={{ y: -1 }}
                                        whileTap={{ scale: 0.97 }}
                                        className="inline-flex items-center gap-2 h-11 px-5 rounded-pill bg-brand-500 hover:bg-brand-600 text-ink-inverse font-bold text-sm shadow-card hover:shadow-elevated transition-shadow"
                                    >
                                        <MessageCircle size={16} strokeWidth={2.5} />
                                        <span>צ׳אט קבוצתי</span>
                                    </motion.button>
                                </div>
                            );
                        })()}

                        {/* Items grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                            {selectedCategoryItems.map((item, index) => {
                                const IconComponent = getIconComponent(item.icon);
                                const accent = accentForCategory(item.title || 'x');
                                const isOwner = chatId && chatId !== 'guest' && String(item.user_id) === String(chatId);
                                return (
                                    <motion.div
                                        key={item.entity_id}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ delay: Math.min(index * 0.035, 0.32), duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                                        whileHover={{ y: -2 }}
                                        className="group relative overflow-hidden rounded-2xl bg-surface border border-line-subtle hover:border-line shadow-card hover:shadow-elevated transition-shadow duration-300 p-5 flex flex-col"
                                    >
                                        <div aria-hidden="true" className={`pointer-events-none absolute -top-10 -start-8 w-24 h-24 rounded-pill blur-2xl opacity-60 ${accent.blob}`} />

                                        {/* Top row: icon + type chip + owner actions */}
                                        <div className="relative flex items-start justify-between gap-3 mb-3">
                                            <div className={`inline-flex items-center justify-center w-11 h-11 rounded-xl shadow-sm text-white ${accent.gradient}`}>
                                                <IconComponent size={22} strokeWidth={2.25} />
                                            </div>
                                            <div className="flex items-center gap-1.5">
                                                <span className="inline-flex items-center text-2xs font-bold uppercase tracking-wide px-2.5 py-1 rounded-pill bg-brand-50 text-brand-700 ring-1 ring-brand-200">
                                                    {item.type === 'skill' ? 'כישור' : 'ידע'}
                                                </span>
                                                {isOwner && (
                                                    <>
                                                        <button
                                                            type="button"
                                                            onClick={() => onEditSkill(item)}
                                                            className="w-8 h-8 rounded-pill flex items-center justify-center bg-surface ring-1 ring-line text-ink-muted hover:text-brand-700 hover:ring-brand-200 transition-all active:scale-95"
                                                            title="ערוך"
                                                        >
                                                            <Edit2 className="w-3.5 h-3.5" strokeWidth={2} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() => onDeleteSkill(item.entity_id)}
                                                            className="w-8 h-8 rounded-pill flex items-center justify-center bg-surface ring-1 ring-line text-ink-muted hover:text-danger-600 hover:ring-danger-100 transition-all active:scale-95"
                                                            title="מחק"
                                                        >
                                                            <Trash2 className="w-3.5 h-3.5" strokeWidth={2} />
                                                        </button>
                                                    </>
                                                )}
                                            </div>
                                        </div>

                                        {/* Title + description */}
                                        <h3 className="relative font-heading font-bold text-base text-ink-strong leading-tight mb-1.5 line-clamp-2">
                                            {item.title}
                                        </h3>
                                        {item.description && (
                                            <p className="relative text-sm text-ink-muted leading-relaxed line-clamp-3 mb-4">
                                                {item.description}
                                            </p>
                                        )}

                                        {/* Contact */}
                                        {(item.phone || item.email) && (
                                            <div className="relative flex flex-wrap gap-1.5 mb-4">
                                                {item.phone && (
                                                    <a
                                                        href={item.has_whatsapp ? `https://wa.me/${item.phone.replace(/\D/g, '')}` : `tel:${item.phone}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-pill bg-surface-muted ring-1 ring-line-subtle text-xs font-semibold text-ink hover:text-brand-700 hover:ring-brand-200 transition-all"
                                                    >
                                                        <Phone className="w-3.5 h-3.5" strokeWidth={2} />
                                                        <span dir="ltr">{item.phone}</span>
                                                        {item.has_whatsapp && (
                                                            <span className="text-2xs bg-success-500 text-white px-1.5 py-0.5 rounded-pill font-bold">WhatsApp</span>
                                                        )}
                                                    </a>
                                                )}
                                                {item.email && (
                                                    <a
                                                        href={`mailto:${item.email}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-pill bg-surface-muted ring-1 ring-line-subtle text-xs font-semibold text-ink hover:text-brand-700 hover:ring-brand-200 transition-all max-w-full"
                                                    >
                                                        <Mail className="w-3.5 h-3.5 shrink-0" strokeWidth={2} />
                                                        <span className="truncate">{item.email}</span>
                                                    </a>
                                                )}
                                            </div>
                                        )}

                                        {/* Owner footer */}
                                        <div className="relative flex items-center gap-2.5 pt-3 mt-auto border-t border-line-subtle">
                                            {item.user_photo ? (
                                                <img
                                                    src={item.user_photo}
                                                    alt={item.user_name}
                                                    className="w-8 h-8 rounded-pill object-cover ring-2 ring-surface shadow-sm"
                                                />
                                            ) : (
                                                <div className={`w-8 h-8 rounded-pill flex items-center justify-center text-white font-bold text-xs shadow-sm ${accent.gradient}`}>
                                                    {item.user_name?.[0] || 'א'}
                                                </div>
                                            )}
                                            <p className="text-xs font-semibold text-ink-strong truncate flex-1">
                                                {item.user_name || 'אנונימי'}
                                            </p>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            <AnimatePresence>
                {showChat && selectedCategory && (
                    <CategoryGroupChat
                        category={selectedCategory}
                        zoneId={zoneId}
                        chatId={chatId}
                        userName={userName}
                        userPhoto={userPhoto}
                        onClose={() => setShowChat(false)}
                    />
                )}
            </AnimatePresence>
        </div>
    );
};
